import { Injectable, computed, inject } from '@angular/core';
import { environment } from '@environments/environment';
import { AuthService } from './auth.service';

/**
 * First-Touch-Attribution eines Besuchs (Issue #384). Wird beim ersten Aufruf
 * aus der Landing-URL gelesen und im localStorage gemerkt, bis der Besucher
 * einen Account hat (Registrierung / Gast-Übernahme) – dann landet sie auf dem
 * User-Datensatz (referred_by, acquisition_campaign, acquisition_source, …).
 */
export interface FunnelAttribution {
  /** Affiliate-/Ambassador-Code aus ?ref= */
  ref: string;
  /** Kampagnen-Slug aus ?c= / ?utm_campaign= (QR-Codes, Print, Ads) */
  campaign: string;
  /** utm_source (z. B. tiktok, instagram, newsletter) */
  source: string;
  /** utm_medium (z. B. social, affiliate, qr, email) */
  medium: string;
  /** utm_content (z. B. hero_cta, bio_link) */
  content: string;
  /** Pfad + Query des ersten Aufrufs */
  landing: string;
  /** ISO-Zeitpunkt des ersten Aufrufs */
  firstSeen: string;
}

/** Definition eines A/B-Tests: Schlüssel + Varianten (deterministisch je Session verteilt). */
export interface FunnelExperiment {
  key: string;
  variants: string[];
}

/**
 * Aktive Experimente. Ein Experiment entfernen = Variante festschreiben.
 * Die Zuweisung ist deterministisch (Hash aus Session-ID + Key), damit ein
 * Besucher über Reloads hinweg dieselbe Variante sieht.
 */
export const FUNNEL_EXPERIMENTS: FunnelExperiment[] = [
  // CTA-Wording auf der Startseite /start
  { key: 'start_cta', variants: ['testen', 'starten'] }
];

const KEY_ATTRIBUTION = 'kk_attribution';
const KEY_SESSION = 'kk_sid';
const KEY_KNOWN_USER = 'kk_known_user';
const KEY_VARIANTS = 'kk_variants';

@Injectable({ providedIn: 'root' })
export class FunnelService {
  private readonly authService = inject(AuthService);

  /** True, solange der angemeldete Nutzer ein Gast-Account ohne bestätigte E-Mail ist. */
  readonly isGuest = computed(() => !!(this.authService.user() as any)?.is_guest);

  // ---------------------------------------------------------------------------
  // Attribution
  // ---------------------------------------------------------------------------

  /**
   * Liest ref/c/utm_* aus der aktuellen URL und merkt sie (first touch wins).
   * Muss beim App-Start laufen, bevor der Router die Query-Parameter verwirft.
   */
  captureAttribution(): void {
    try {
      const params = new URLSearchParams(window.location.search);
      const stored = this.getAttribution();
      if (stored) {
        // Bereits attribuiert – nur fehlende Felder nachtragen (z. B. ref nach c)
        let changed = false;
        const ref = this.slug(params.get('ref'));
        if (ref && !stored.ref) { stored.ref = ref; changed = true; }
        const campaign = this.slug(params.get('c') || params.get('utm_campaign'));
        if (campaign && !stored.campaign) { stored.campaign = campaign; changed = true; }
        if (changed) this.setAttribution(stored);
        this.syncLegacyKeys(stored);
        return;
      }

      const attribution: FunnelAttribution = {
        ref: this.slug(params.get('ref')),
        campaign: this.slug(params.get('c') || params.get('utm_campaign')),
        source: this.slug(params.get('utm_source')),
        medium: this.slug(params.get('utm_medium')),
        content: this.slug(params.get('utm_content')),
        landing: (window.location.pathname + window.location.search).slice(0, 200),
        firstSeen: new Date().toISOString()
      };

      // Ohne Parameter: Referrer als grobe Quelle (z. B. tiktok.com → tiktok)
      if (!attribution.source && document.referrer) {
        try {
          const host = new URL(document.referrer).hostname.replace(/^www\./, '');
          if (host && !host.endsWith('klausurenkiste.de')) {
            attribution.source = this.slug(host.split('.')[0]);
            attribution.medium = attribution.medium || 'referral';
          }
        } catch { /* ignore */ }
      }

      this.setAttribution(attribution);
      this.syncLegacyKeys(attribution);
    } catch { /* storage unavailable (private mode) — non-fatal */ }
  }

  getAttribution(): FunnelAttribution | null {
    try {
      const raw = localStorage.getItem(KEY_ATTRIBUTION);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed as FunnelAttribution : null;
    } catch {
      return null;
    }
  }

  /** Attribution + Variante als flache Felder für Backend-Payloads (Registrierung, Gast-Session). */
  attributionPayload(): Record<string, string> {
    const a = this.getAttribution();
    const payload: Record<string, string> = {};
    if (a?.ref) payload['ref'] = a.ref;
    if (a?.campaign) payload['campaign'] = a.campaign;
    if (a?.source) payload['source'] = a.source;
    if (a?.medium) payload['medium'] = a.medium;
    if (a?.content) payload['content'] = a.content;
    if (a?.landing) payload['landing'] = a.landing;
    const variant = this.variantSummary();
    if (variant) payload['variant'] = variant;
    return payload;
  }

  private setAttribution(a: FunnelAttribution): void {
    try { localStorage.setItem(KEY_ATTRIBUTION, JSON.stringify(a)); } catch { /* non-fatal */ }
  }

  /** Bestehende Consumer (AffiliateService, RegisterService) lesen kk_ref / kk_campaign. */
  private syncLegacyKeys(a: FunnelAttribution): void {
    try {
      if (a.ref && !localStorage.getItem('kk_ref')) localStorage.setItem('kk_ref', a.ref);
      if (a.campaign && !localStorage.getItem('kk_campaign')) localStorage.setItem('kk_campaign', a.campaign);
    } catch { /* non-fatal */ }
  }

  private slug(v: string | null | undefined): string {
    return (v || '').trim().toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 40);
  }

  // ---------------------------------------------------------------------------
  // Session / bekannter Nutzer
  // ---------------------------------------------------------------------------

  /** Anonyme Session-ID (localStorage), verbindet Events vor und nach der Registrierung. */
  sessionId(): string {
    try {
      let sid = localStorage.getItem(KEY_SESSION);
      if (!sid) {
        sid = 's_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 12);
        localStorage.setItem(KEY_SESSION, sid);
      }
      return sid;
    } catch {
      return 's_' + Date.now().toString(36);
    }
  }

  /**
   * Merkt, dass dieses Gerät schon einen echten Account hatte. Neue Besucher
   * ohne diesen Marker werden von "/" nach /start (ausprobieren) geleitet statt
   * zum Login.
   */
  markKnownUser(): void {
    try { localStorage.setItem(KEY_KNOWN_USER, '1'); } catch { /* non-fatal */ }
  }

  isKnownUser(): boolean {
    try { return localStorage.getItem(KEY_KNOWN_USER) === '1'; } catch { return false; }
  }

  // ---------------------------------------------------------------------------
  // Experimente
  // ---------------------------------------------------------------------------

  /** Variante eines Experiments für diese Session (deterministisch, persistiert). */
  variant(key: string): string {
    const exp = FUNNEL_EXPERIMENTS.find(x => x.key === key);
    if (!exp || exp.variants.length === 0) return '';
    try {
      const stored = JSON.parse(localStorage.getItem(KEY_VARIANTS) || '{}');
      if (stored[key] && exp.variants.includes(stored[key])) return stored[key];
      const idx = this.hash(this.sessionId() + '|' + key) % exp.variants.length;
      stored[key] = exp.variants[idx];
      localStorage.setItem(KEY_VARIANTS, JSON.stringify(stored));
      return stored[key];
    } catch {
      return exp.variants[0];
    }
  }

  /** Alle zugewiesenen Varianten als "key:variant,key:variant" (landet auf dem User + in Events). */
  variantSummary(): string {
    return FUNNEL_EXPERIMENTS
      .map(exp => `${exp.key}:${this.variant(exp.key)}`)
      .join(',')
      .slice(0, 60);
  }

  private hash(input: string): number {
    let h = 2166136261;
    for (let i = 0; i < input.length; i++) {
      h ^= input.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return Math.abs(h >>> 0);
  }

  // ---------------------------------------------------------------------------
  // Events
  // ---------------------------------------------------------------------------

  /**
   * Fire-and-forget Funnel-Event (kein PII). Events:
   *  start_view, start_cta_click, editor_open, submit_click, claim_open,
   *  claim_email_sent, claim_verified, result_view, paywall_view,
   *  trial_checkout_start, single_checkout_start, checkout_success, register_start
   */
  track(event: string, props: { caseId?: string; meta?: Record<string, unknown> } = {}): void {
    try {
      const a = this.getAttribution();
      const body = JSON.stringify({
        event,
        sessionId: this.sessionId(),
        ref: a?.ref || '',
        campaign: a?.campaign || '',
        source: a?.source || '',
        medium: a?.medium || '',
        variant: this.variantSummary(),
        caseId: props.caseId || '',
        path: (window.location.pathname || '').slice(0, 200),
        meta: props.meta || {}
      });
      const url = `${environment.pocketbaseUrl}/api/funnel/track`;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      const token = this.authService.authStore()?.token;
      if (token) headers['Authorization'] = token;
      fetch(url, { method: 'POST', headers, body, keepalive: true }).catch(() => { /* ignore */ });
    } catch { /* never break the UI for analytics */ }
  }
}
